// src/components/hooks/usePersistedLeadWorkspace.ts
import { useEffect } from "react";
import type { Lead } from "@/types/lead";

export type UsePersistedLeadWorkspaceArgs<TSalesperson, TLocation> = {
  storageKey: string;

  // setters
  setSalesperson: (v: TSalesperson | null) => void;
  setLocation: (v: TLocation | null) => void;
  setLeads: (v: Lead[]) => void;

  // current values to persist
  salesperson: TSalesperson | null;
  location: TLocation | null;
  leads: Lead[];
};

type PersistedPayload<TSalesperson, TLocation> = {
  salesperson?: TSalesperson | null;
  location?: TLocation | null;
  leads?: Lead[];
};

export function usePersistedLeadWorkspace<TSalesperson, TLocation>({
  storageKey,
  setSalesperson,
  setLocation,
  setLeads,
  salesperson,
  location,
  leads,
}: UsePersistedLeadWorkspaceArgs<TSalesperson, TLocation>) {
  // Load on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return;

      const parsed = JSON.parse(saved) as PersistedPayload<TSalesperson, TLocation>;

      setSalesperson(parsed.salesperson ?? null);
      setLocation(parsed.location ?? null);
      setLeads(Array.isArray(parsed.leads) ? parsed.leads : []);
    } catch (e) {
      console.warn(`[persist] failed to load ${storageKey}`, e);
      // If corrupted, do nothing (do not wipe automatically)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist whenever these change
  useEffect(() => {
    try {
      const payload: PersistedPayload<TSalesperson, TLocation> = { salesperson, location, leads };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (e) {
      console.warn(`[persist] failed to save ${storageKey}`, e);
    }
  }, [storageKey, salesperson, location, leads]);
}